import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Globe, Users, Heart } from 'lucide-react';
import { sendCompanyRegistrationEmail, CompanyRegistrationData } from '../services/companyRegistrationService';
import toast from 'react-hot-toast';
import Footer from '../components/Footer';
import { useTranslation } from '../hooks/use-i18n';

const CompanyRegistration: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<CompanyRegistrationData>({
    companyName: '',
    companyWebsite: '',
    employeeCount: '',
    estimatedPlayers: '',
    contactName: '',
    contactEmail: '',
    additionalInfo: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const success = await sendCompanyRegistrationEmail(formData);
      
      if (success) {
        toast.success('Registration submitted successfully!');
        navigate('/company-registration/success');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      toast.error('Failed to submit registration. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen gradient-bg-darker">
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-3 xs:px-4 sm:px-4 lg:px-8 py-12 sm:py-16 lg:py-24">
        <div className="text-center mb-12">
          <h1 className="font-display text-display-1 font-bold mb-4 sm:mb-6 lg:mb-8 leading-tight">
            <span className="text-foreground">{t('company.heroTitle')}</span>
            <br />
            <span className="text-display-2 text-primary">{t('company.heroTitle2')}</span>
          </h1>
          <p className="font-sans text-body-lg font-medium text-foreground/95 max-w-3xl mx-auto mb-6 leading-relaxed">
            {t('company.heroSubtitle')}
          </p>
          <p className="font-sans text-body-lg font-medium text-foreground/95 max-w-2xl mx-auto mb-6 sm:mb-8 leading-relaxed">
            {t('company.heroPerfectFor')}
          </p>
          <div className="flex justify-center">
            <button
              onClick={() => {
                const registrationSection = document.getElementById('register-company');
                if (registrationSection) {
                  registrationSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className="bg-primary hover:opacity-90 text-primary-foreground px-6 sm:px-8 py-3 rounded-xl text-nav font-semibold transition-all"
            >
              {t('company.register')}
            </button>
          </div>
        </div>

        {/* Feature Cards: Chess Arena, Public & Room Predictions, Team bonds */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12">
          <div className="rounded-2xl sm:rounded-3xl p-5 sm:p-6 text-center bg-white/5 border border-white/10 hover:border-primary/40 hover:bg-white/[0.07] transition-all duration-300 min-w-0 shadow-lg shadow-black/20">
            <div className="w-16 h-16 rounded-2xl bg-primary/35 flex items-center justify-center mb-4 mx-auto ring-2 ring-primary/30">
              <Trophy className="w-8 h-8 text-primary-foreground" strokeWidth={2.25} />
            </div>
            <h3 className="font-display text-lg sm:text-xl font-bold mb-2 text-primary">{t('company.card1Title')}</h3>
            <p className="font-sans text-sm sm:text-base text-foreground/90 leading-snug">
              {t('company.card1Desc')}
            </p>
          </div>
          <div className="rounded-2xl sm:rounded-3xl p-5 sm:p-6 text-center bg-white/5 border border-white/10 hover:border-success/40 hover:bg-white/[0.07] transition-all duration-300 min-w-0 shadow-lg shadow-black/20">
            <div className="w-16 h-16 rounded-2xl bg-success/35 flex items-center justify-center mb-4 mx-auto ring-2 ring-success/30">
              <Globe className="w-8 h-8 text-success-foreground" strokeWidth={2.25} />
            </div>
            <h3 className="font-display text-lg sm:text-xl font-bold mb-2 text-success">{t('company.card2Title')}</h3>
            <p className="font-sans text-sm sm:text-base text-foreground/90 leading-snug">
              {t('company.card2Desc')}
            </p>
          </div>
          <div className="rounded-2xl sm:rounded-3xl p-5 sm:p-6 text-center bg-white/5 border border-white/10 hover:border-chess/40 hover:bg-white/[0.07] transition-all duration-300 min-w-0 shadow-lg shadow-black/20">
            <div className="w-16 h-16 rounded-2xl bg-chess/35 flex items-center justify-center mb-4 mx-auto ring-2 ring-chess/30">
              <Users className="w-8 h-8 text-chess-foreground" strokeWidth={2.25} />
            </div>
            <h3 className="font-display text-lg sm:text-xl font-bold mb-2 text-chess">{t('company.card3Title')}</h3>
            <p className="font-sans text-sm sm:text-base text-foreground/90 leading-snug">
              {t('company.card3Desc')}
            </p>
          </div>
          <div className="rounded-2xl sm:rounded-3xl p-5 sm:p-6 text-center bg-white/5 border border-white/10 hover:border-primary/40 hover:bg-white/[0.07] transition-all duration-300 min-w-0 shadow-lg shadow-black/20">
            <div className="w-16 h-16 rounded-2xl bg-primary/35 flex items-center justify-center mb-4 mx-auto ring-2 ring-primary/30">
              <Heart className="w-8 h-8 text-primary-foreground" strokeWidth={2.25} />
            </div>
            <h3 className="font-display text-lg sm:text-xl font-bold mb-2 text-primary">{t('company.card4Title')}</h3>
            <p className="font-sans text-sm sm:text-base text-foreground/90 leading-snug">
              {t('company.card4Desc')}
            </p>
          </div>
        </div>

        {/* Ranking Examples Section */}
        <div className="mb-12 sm:mb-16">
          <h2 className="font-display text-display-1 font-bold text-center mb-8 sm:mb-12 leading-tight">
            <span className="text-foreground">{t('company.uniteTitle')}</span>
            <br />
            <span className="text-display-2 text-primary">{t('company.uniteSubtitle')}</span>
          </h2>
          <p className="text-center font-sans text-body-lg font-medium text-foreground/95 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed">
            {t('company.uniteDesc')}
          </p>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-12">
            {/* Team Rankings */}
            <div className="blue-sky-card rounded-xl p-6 sm:p-8 border border-white/20">
              <h3 className="text-display-2 font-bold mb-4 sm:mb-6 text-foreground flex items-center">
                <span className="text-display-2 mr-2">🏆</span>
                Let your internal departments compete
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left py-2 sm:py-3 text-foreground/90 text-nav font-semibold">Rank</th>
                      <th className="text-left py-2 sm:py-3 text-foreground/90 text-nav font-semibold">Department</th>
                      <th className="text-right py-2 sm:py-3 text-foreground/90 text-nav font-semibold">Games</th>
                      <th className="text-right py-2 sm:py-3 text-foreground/90 text-nav font-semibold">Win %</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-white/10 bg-white/5">
                      <td className="py-2 sm:py-3">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-500/90 text-primary-foreground text-small font-bold">1</span>
                      </td>
                      <td className="py-2 sm:py-3 text-foreground font-semibold">Marketing</td>
                      <td className="py-2 sm:py-3 text-right text-foreground/95">24</td>
                      <td className="py-2 sm:py-3 text-right text-foreground/95">78%</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-2 sm:py-3">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-400/90 text-primary-foreground text-small font-bold">2</span>
                      </td>
                      <td className="py-2 sm:py-3 text-foreground">Sales</td>
                      <td className="py-2 sm:py-3 text-right text-foreground/95">28</td>
                      <td className="py-2 sm:py-3 text-right text-foreground/95">64%</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-2 sm:py-3">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-700/90 text-primary-foreground text-small font-bold">3</span>
                      </td>
                      <td className="py-2 sm:py-3 text-foreground">Product</td>
                      <td className="py-2 sm:py-3 text-right text-foreground/95">22</td>
                      <td className="py-2 sm:py-3 text-right text-foreground/95">59%</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-2 sm:py-3 text-foreground/95">4</td>
                      <td className="py-2 sm:py-3 text-foreground">Engineering</td>
                      <td className="py-2 sm:py-3 text-right text-foreground/95">26</td>
                      <td className="py-2 sm:py-3 text-right text-foreground/95">54%</td>
                    </tr>
                    <tr>
                      <td className="py-2 sm:py-3 text-foreground/95">5</td>
                      <td className="py-2 sm:py-3 text-foreground">Accounting</td>
                      <td className="py-2 sm:py-3 text-right text-foreground/95">20</td>
                      <td className="py-2 sm:py-3 text-right text-foreground/95">45%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Player Rankings */}
            <div className="blue-sky-card rounded-xl p-6 sm:p-8 border border-white/20">
              <h3 className="text-display-2 font-bold mb-4 sm:mb-6 text-foreground flex items-center">
                <span className="text-display-2 mr-2">👑</span>
                Celebrate your top players
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left py-2 sm:py-3 text-foreground/90 text-nav font-semibold">Rank</th>
                      <th className="text-left py-2 sm:py-3 text-foreground/90 text-nav font-semibold">Player</th>
                      <th className="text-left py-2 sm:py-3 text-foreground/90 text-nav font-semibold">Department</th>
                      <th className="text-right py-2 sm:py-3 text-foreground/90 text-nav font-semibold">Wins</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-white/10 bg-white/5">
                      <td className="py-2 sm:py-3">
                        <div className="relative inline-block">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-500/90 text-primary-foreground text-small font-bold">1</span>
                          <span className="absolute -top-1 -right-1 text-small">🏅</span>
                        </div>
                      </td>
                      <td className="py-2 sm:py-3 text-foreground font-semibold">Emily Chen</td>
                      <td className="py-2 sm:py-3 text-foreground/95">Marketing</td>
                      <td className="py-2 sm:py-3 text-right text-foreground/95">8</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-2 sm:py-3">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-400/90 text-primary-foreground text-small font-bold">2</span>
                      </td>
                      <td className="py-2 sm:py-3 text-foreground">David Kim</td>
                      <td className="py-2 sm:py-3 text-foreground/95">Engineering</td>
                      <td className="py-2 sm:py-3 text-right text-foreground/95">7</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-2 sm:py-3">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-700/90 text-primary-foreground text-small font-bold">3</span>
                      </td>
                      <td className="py-2 sm:py-3 text-foreground">Sarah Johnson</td>
                      <td className="py-2 sm:py-3 text-foreground/95">Sales</td>
                      <td className="py-2 sm:py-3 text-right text-foreground/95">6</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-2 sm:py-3 text-foreground/95">4</td>
                      <td className="py-2 sm:py-3 text-foreground">James Smith</td>
                      <td className="py-2 sm:py-3 text-foreground/95">Product</td>
                      <td className="py-2 sm:py-3 text-right text-foreground/95">5</td>
                    </tr>
                    <tr>
                      <td className="py-2 sm:py-3 text-foreground/95">5</td>
                      <td className="py-2 sm:py-3 text-foreground">Michael Davis</td>
                      <td className="py-2 sm:py-3 text-foreground/95">Sales</td>
                      <td className="py-2 sm:py-3 text-right text-foreground/95">4</td>
                    </tr>
                  </tbody>
                </table>
                <p className="text-small text-foreground/90 mt-4 italic">
                  *Only top players are shown in the ranking after the qualification rounds
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="mb-12 sm:mb-16 py-8 sm:py-12 px-4 sm:px-8 bg-gradient-to-r from-white/5 to-white/10 backdrop-blur-sm rounded-xl border border-white/10">
          <h2 className="font-display text-display-1 font-bold text-center mb-8 sm:mb-12 text-foreground">
            What our clients say
          </h2>
          
          <div className="max-w-4xl mx-auto blue-sky-card rounded-xl p-6 sm:p-8 lg:p-10 border border-white/20 overflow-hidden">
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/3 p-4 sm:p-6 md:p-8 flex justify-center md:border-r border-white/10 mb-6 md:mb-0">
                <div className="h-24 w-24 sm:h-32 sm:w-32 rounded-full bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center border-2 border-white/30">
                  <span className="text-3xl sm:text-4xl font-bold text-foreground">NS</span>
                </div>
              </div>
              
              <div className="md:w-2/3 p-4 sm:p-6 md:p-8 flex flex-col justify-center">
                <div className="mb-4 text-sky-400">
                  <span className="text-4xl sm:text-5xl">"</span>
                </div>
                
                <p className="text-body-lg italic mb-6 text-foreground/95 leading-relaxed">
                  The tournament was a fantastic way to connect with colleagues we don't normally interact with. The grand final, played in front of the entire company, was a memorable moment that really brought our culture to life!
                </p>
                
                <div className="mt-auto">
                  <h4 className="font-bold text-display-2 mb-1 text-foreground">Nil Sanz</h4>
                  <p className="text-body-lg text-foreground/90">Engineering Manager & Staff Engineer @ Lighthouse</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Registration Form */}
        <div id="register-company" className="max-w-2xl mx-auto scroll-mt-20">
          <div className="glass-strong rounded-xl sm:rounded-2xl p-6 sm:p-8 lg:p-10 border border-white/15">
            <h2 className="font-display text-display-2 font-bold text-center mb-6 text-foreground">
              {t('company.formTitle')}
            </h2>
            <p className="text-center font-sans text-body-lg text-foreground/95 mb-8 leading-relaxed">
              {t('company.formIntro')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="companyName" className="block text-nav font-medium text-foreground/90 mb-2">
                  {t('company.companyName')}
                </label>
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-foreground placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition-all"
                  placeholder={t('company.placeholderCompany')}
                />
              </div>

              <div>
                <label htmlFor="companyWebsite" className="block text-nav font-medium text-foreground/90 mb-2">
                  {t('company.companyWebsite')}
                </label>
                <input
                  id="companyWebsite"
                  name="companyWebsite"
                  type="url"
                  value={formData.companyWebsite}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-foreground placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition-all"
                  placeholder={t('company.placeholderWebsite')}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="employeeCount" className="block text-nav font-medium text-foreground/90 mb-2">
                    {t('company.employeeCount')}
                  </label>
                  <input
                    id="employeeCount"
                    name="employeeCount"
                    type="number"
                    value={formData.employeeCount}
                    onChange={handleChange}
                    required
                    min="8"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-foreground placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition-all"
                    placeholder="100"
                  />
                </div>

                <div>
                  <label htmlFor="estimatedPlayers" className="block text-nav font-medium text-foreground/90 mb-2">
                    {t('company.estimatedPlayers')}
                  </label>
                  <input
                    id="estimatedPlayers"
                    name="estimatedPlayers"
                    type="number"
                    value={formData.estimatedPlayers}
                    onChange={handleChange}
                    required
                    min="8"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-foreground placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition-all"
                    placeholder="20"
                  />
                  <p className="text-small text-foreground/90 mt-1">{t('company.minPlayers')}</p>
                </div>
              </div>

              <div>
                <label htmlFor="contactName" className="block text-nav font-medium text-foreground/90 mb-2">
                  {t('company.contactName')}
                </label>
                <input
                  id="contactName"
                  name="contactName"
                  type="text"
                  value={formData.contactName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-foreground placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition-all"
                  placeholder={t('company.placeholderContactName')}
                />
              </div>

              <div>
                <label htmlFor="contactEmail" className="block text-nav font-medium text-foreground/90 mb-2">
                  {t('company.contactEmail')}
                </label>
                <input
                  id="contactEmail"
                  name="contactEmail"
                  type="email"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-foreground placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition-all"
                  placeholder={t('company.placeholderEmail')}
                />
              </div>

              <div>
                <label htmlFor="additionalInfo" className="block text-nav font-medium text-foreground/90 mb-2">
                  {t('company.additionalInfo')}
                </label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-foreground placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition-all resize-none"
                  placeholder={t('company.placeholderAdditional')}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary hover:opacity-90 text-primary-foreground px-6 py-3 rounded-xl text-nav font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? t('company.submitting') : t('company.submit')}
              </button>

              <p className="text-center text-body-lg text-foreground/95">
                {t('company.formFooter')}
              </p>
            </form>
          </div>
        </div>
      </section>

      <Footer className="mt-12" />
    </div>
  );
};

export default CompanyRegistration;

